<template>
  <AuthShell
    :title="$t('login.heroTitle')"
    :subtitle="$t('login.heroSubtitle')"
  >
    <div class="space-y-6">
      <div>
        <p class="text-sm uppercase tracking-[0.3em] text-steel-400">{{ $t('login.eyebrow') }}</p>
        <h2 class="mt-3 font-display text-2xl font-semibold text-white">{{ $t('login.title') }}</h2>
      </div>

      <button
        type="button"
        class="flex w-full items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-medium text-white transition hover:border-white/30 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isLoading"
        @click="signInWithGoogle"
      >
        <svg class="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#FFC107"
            d="M43.611 20.083H42V20H24v8h11.303C33.45 32.91 29.145 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.96 3.04l5.657-5.657C34.046 6.053 29.247 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.651-.389-3.917Z"
          />
          <path
            fill="#FF3D00"
            d="M6.306 14.691 12.17 18.987C13.758 15.147 18.143 12 24 12c3.059 0 5.842 1.154 7.96 3.04l5.657-5.657C34.046 6.053 29.247 4 24 4 16.318 4 9.656 8.348 6.306 14.691Z"
          />
          <path
            fill="#4CAF50"
            d="M24 44c5.047 0 9.749-1.932 13.258-5.077l-6.121-5.182C29.065 35.481 26.645 36 24 36c-5.126 0-9.42-3.063-11.29-7.43l-5.889 4.54C9.129 39.556 16.073 44 24 44Z"
          />
          <path
            fill="#1976D2"
            d="M43.611 20.083H42V20H24v8h11.303a12.03 12.03 0 0 1-4.166 5.741l.002-.001 6.121 5.182C36.858 39.294 44 34 44 24c0-1.341-.138-2.651-.389-3.917Z"
          />
        </svg>
        {{ $t('login.google') }}
      </button>

      <div class="flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-steel-400">
        <span class="h-px flex-1 bg-white/10"></span>
        {{ $t('login.or') }}
        <span class="h-px flex-1 bg-white/10"></span>
      </div>

      <form class="space-y-5" @submit.prevent="signInWithEmail">
        <label class="block text-sm text-steel-200">
          {{ $t('login.email') }}
          <input
            type="email"
            placeholder="driver@drvn.co"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            v-model.trim="email"
            autocomplete="email"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('login.password') }}
          <input
            type="password"
            placeholder="••••••••"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            v-model="password"
            autocomplete="current-password"
          />
        </label>

        <div class="flex flex-wrap items-center justify-between gap-3 text-xs text-steel-400">
          <label class="flex items-center gap-2">
            <input v-model="rememberDevice" type="checkbox" class="h-4 w-4 rounded border-white/20 bg-midnight-800 text-neon-400" />
            {{ $t('login.rememberDevice') }}
          </label>
          <router-link class="text-neon-400 hover:text-neon-500" to="/forgot-password">{{ $t('login.forgotPassword') }}</router-link>
        </div>

        <button
          type="submit"
          class="w-full rounded-2xl bg-white px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="isLoading"
        >
          {{ isLoading ? $t('login.submitting') : $t('login.submit') }}
        </button>
      </form>

      <p v-if="statusMessage" class="rounded-2xl border border-neon-400/30 bg-neon-400/10 px-4 py-3 text-sm text-neon-100">
        {{ statusMessage }}
      </p>

      <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ errorMessage }}
      </p>

      <p class="text-center text-xs text-steel-400">
        {{ $t('login.newTo') }}
        <router-link class="text-neon-400 hover:text-neon-500" to="/register">{{ $t('login.createAccount') }}</router-link>
      </p>
    </div>
  </AuthShell>
</template>

<script>
import AuthShell from '@/components/auth/AuthShell.vue'
import { supabase, waitForSession } from '@/lib/supabase'
import { getPushPermissionState, isPushSupported, requestPushPermissionAndSubscribe } from '@/lib/push'

export default {
  name: 'LoginView',
  components: {
    AuthShell,
  },
  data() {
    return {
      email: '',
      password: '',
      rememberDevice: true,
      isLoading: false,
      statusMessage: '',
      errorMessage: '',
    }
  },
  mounted() {
    if (this.$route?.query?.banned) {
      this.errorMessage = this.$t('login.banned')
    }
    if (this.$route?.query?.reset === 'success') {
      this.statusMessage = this.$t('login.resetSuccess')
    }
  },
  methods: {
    // Remove Supabase local session key on unload when user did not choose to remember device
    _unloadHandler() {
      try {
        localStorage.removeItem('supabase.auth.token')
      } catch (e) {
        // ignore
      }
    },
    setupRemember(remember) {
      // remove any previous handler
      try {
        window.removeEventListener('beforeunload', this._unloadHandler)
      } catch (e) {
        // ignore
      }

      if (!remember) {
        window.addEventListener('beforeunload', this._unloadHandler)
        // also handle pagehide (some browsers use this)
        window.addEventListener('pagehide', this._unloadHandler)
      } else {
        try {
          window.removeEventListener('pagehide', this._unloadHandler)
        } catch (e) {
          // ignore
        }
      }
    },
    async signInWithEmail() {
      if (!this.email || !this.password) {
        this.errorMessage = this.$t('login.missingCredentials')
        return
      }

      this.isLoading = true
      this.statusMessage = ''
      this.errorMessage = ''
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email: this.email,
          password: this.password,
        })

        if (error) {
          this.errorMessage = error.message
          return
        }

        const session = await waitForSession()
        if (!session?.user?.id) {
          this.errorMessage = 'Login completed, but the session did not finish loading. Please try again.'
          return
        }

        this.setupRemember(this.rememberDevice)
        if (isPushSupported() && getPushPermissionState() === 'default') {
          try {
            await requestPushPermissionAndSubscribe(session)
          } catch (_error) {
            // Non-blocking: the global app banner will keep offering push later.
          }
        }
        await this.$router.replace('/feed')
      } finally {
        this.isLoading = false
      }
    },
    async signInWithGoogle() {
      this.isLoading = true
      this.statusMessage = ''
      this.errorMessage = ''
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://drvnsocial.com/auth/callback',
        },
      })
      this.isLoading = false

      if (error) {
        this.errorMessage = error.message
      }
    },
  },
}
</script>
